/**
 * DriftDetector — stateless helper that computes the context tag
 * appended to every GPS update message sent to Guide. Uses DriftContext
 * for persistent state (lastTs, lastLat/Lon, stationarySince).
 *
 * Returns one of:
 *   ' [FIRST_UPDATE]'        — first position fix ever or after storage clear
 *   ' [RESUMED_AFTER_GAP:X]' — gap > 60min since last fix
 *   ' [STATIONARY:Xmin]'     — within 50m AND stationary >= 3 minutes
 *   ' [CRUISING]'            — moving OR stationary < 3 minutes
 *
 * Ported verbatim from gps-tracker.html `computeContextTag()` to keep
 * the Guide bot contract byte-identical.
 */
export class DriftDetector {
    constructor(_ctx) {
        this._ctx = _ctx;
    }
    /**
     * Compute the context tag for a new position and update the persisted state.
     * Returns the string with a leading space, e.g. " [STATIONARY:4min]".
     */
    computeContextTag(curLat, curLon) {
        const now = Date.now();
        const { lastTs, lastLat, lastLon } = this._ctx;
        let stationarySince = this._ctx.stationarySince;
        const gapMs = lastTs > 0 ? now - lastTs : -1;
        const gapMin = gapMs > 0 ? Math.round(gapMs / 60000) : -1;
        let movedM = 0;
        if (!isNaN(lastLat) && !isNaN(lastLon)) {
            const dLat = (curLat - lastLat) * 111000;
            const dLon = (curLon - lastLon) * 111000 * Math.cos((curLat * Math.PI) / 180);
            movedM = Math.sqrt(dLat * dLat + dLon * dLon);
        }
        let tag = '';
        if (lastTs === 0) {
            tag = ' [FIRST_UPDATE]';
            stationarySince = now;
        }
        else if (gapMin >= DriftDetector.LONG_GAP_MINUTES) {
            const gapLabel = gapMin >= 120 ? Math.round(gapMin / 60) + 'h' : gapMin + 'min';
            tag = ' [RESUMED_AFTER_GAP:' + gapLabel + ']';
            stationarySince = now;
        }
        else if (movedM < DriftDetector.STATIONARY_RADIUS_M) {
            if (stationarySince === 0)
                stationarySince = lastTs;
            const stationaryMin = Math.round((now - stationarySince) / 60000);
            if (stationaryMin >= DriftDetector.STATIONARY_MIN_MINUTES) {
                tag = ' [STATIONARY:' + stationaryMin + 'min]';
            }
            else {
                tag = ' [CRUISING]';
            }
        }
        else {
            tag = ' [CRUISING]';
            stationarySince = now;
        }
        this._ctx.update(curLat, curLon, now, stationarySince);
        return tag;
    }
}
/** Distance in meters below which we consider the user "stationary" */
DriftDetector.STATIONARY_RADIUS_M = 50;
/** Minutes of stationarity before we switch from CRUISING to STATIONARY tag */
DriftDetector.STATIONARY_MIN_MINUTES = 3;
/** Gap in minutes after which we tag RESUMED_AFTER_GAP */
DriftDetector.LONG_GAP_MINUTES = 60;
//# sourceMappingURL=DriftDetector.js.map